import React, { useEffect, useRef, useState } from "react";
import {
  Box, Button, Grid, TextField, Typography, CircularProgress,
  Popover, Divider, Slider, Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import ImageIcon from "@mui/icons-material/Image";
import PaletteIcon from "@mui/icons-material/Palette";
import LoginIcon from "@mui/icons-material/Login";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { HexColorPicker } from "react-colorful";
import {
  useGetCompanySettingsQuery,
  useUpdateCompanySettingsMutation,
} from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import { uploadImage } from "../../Components/UploadFile/upload";
import { useBrand, IBrand, DEFAULT_BRAND, resolveAssetUrl } from "../../Context/BrandContext";
import {
  AppPage, PageHeader, FormCard, ActionButton,
} from "../../Components/ui";
import { NAVY } from "../../theme/tokens";

/** Hex colour field with a swatch that opens a react-colorful picker. */
const ColorField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 0.75 }}>{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ width: 36, height: 36, borderRadius: 1.5, border: "1px solid", borderColor: "divider", bgcolor: value, cursor: "pointer", flexShrink: 0 }}
        />
        <TextField
          size="small" fullWidth value={value}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ style: { fontSize: 13 } }}
        />
      </Box>
      <Popover
        open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: { p: 1.5, borderRadius: 2 } }}
      >
        <HexColorPicker color={value} onChange={onChange} />
      </Popover>
    </Box>
  );
};

/** Image upload tile + "or paste a URL" — used for logos / favicon / login bg. */
const ImageField: React.FC<{
  label: string; value: string; onChange: (v: string) => void; tall?: boolean;
}> = ({ label, value, onChange, tall }) => {
  const { showErrorMessage } = useNotifier();
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const preview = resolveAssetUrl(value);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp", "image/x-icon", "image/vnd.microsoft.icon"].includes(file.type)) {
      showErrorMessage("Must be an image"); return;
    }
    if (file.size > 2048 * 1024) { showErrorMessage("Image must be under 2 MB"); return; }
    try {
      setBusy(true);
      const path = await uploadImage(file);
      if (path) onChange(path);
    } catch {
      showErrorMessage("Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 0.75 }}>{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{
          width: tall ? 120 : 64, height: 64, borderRadius: 2, bgcolor: "background.default",
          border: preview ? "1px solid" : "2px dashed",
          borderColor: "divider",
          display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0,
        }}>
          {busy ? <CircularProgress size={20} sx={{ color: NAVY }} />
            : preview ? <img src={preview} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            : <ImageIcon sx={{ fontSize: 24, color: "text.disabled" }} />}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <input ref={inputRef} accept="image/*,.ico" style={{ display: "none" }} type="file" onChange={handleFile} />
            <Button size="small" variant="outlined" onClick={() => inputRef.current?.click()}
              startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 11, fontWeight: 600 }}>
              {preview ? "Change" : "Upload"}
            </Button>
            {value && (
              <Button size="small" variant="outlined" color="error" onClick={() => onChange("")}
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 11, fontWeight: 600, minWidth: 0, px: 1 }}>
                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
              </Button>
            )}
          </Box>
          <TextField size="small" fullWidth placeholder="…or paste an image URL"
            value={value} onChange={(e) => onChange(e.target.value)}
            inputProps={{ style: { fontSize: 12 } }} />
        </Box>
      </Box>
    </Box>
  );
};

const CompanyBranding: React.FC = () => {
  const { data, isLoading } = useGetCompanySettingsQuery();
  const [updateSettings, { isLoading: saving }] = useUpdateCompanySettingsMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const { refresh } = useBrand();

  const [form, setForm] = useState<IBrand>(DEFAULT_BRAND);

  useEffect(() => {
    if (data?.data) setForm({ ...DEFAULT_BRAND, ...(data.data as Partial<IBrand>) });
  }, [data]);

  const set = (key: keyof IBrand) => (value: any) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!form.company_name?.trim()) { showErrorMessage("Company name is required"); return; }
    try {
      const res: any = await updateSettings(form).unwrap();
      if (!res.status) { showErrorMessage(res.message); return; }
      showMessage("Branding saved — applying across the app");
      await refresh(); // re-pull + re-apply CSS vars / title / favicon immediately
    } catch {
      showErrorMessage("Something went wrong while saving");
    }
  };

  if (isLoading) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <CircularProgress size={28} sx={{ color: NAVY }} />
    </Box>
  );

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<PaletteIcon />}
          title="Company Branding"
          subtitle="White-label your platform — name, logos, login page and theme colours"
          actions={
            <ActionButton
              tone="gold"
              startIcon={<SaveIcon />}
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? "Saving…" : "Save Branding"}
            </ActionButton>
          }
        />
      }
    >
      <Grid container spacing={3}>
        {/* Left column */}
        <Grid item xs={12} lg={7}>
          <Box mb={3}>
            <FormCard
              title="Company Details"
              subtitle="Name, website and description"
              icon={<BusinessIcon />}
              required
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Company Name *" placeholder="INTALK"
                    value={form.company_name} onChange={(e: any) => set("company_name")(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Website" placeholder="https://intalk.ca"
                    value={form.website_url || ""} onChange={(e: any) => set("website_url")(e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Description" placeholder="Short description of your company"
                    multiline rows={2} value={form.description || ""} onChange={(e: any) => set("description")(e.target.value)} />
                </Grid>
              </Grid>
            </FormCard>
          </Box>

          <Box mb={3}>
            <FormCard
              title="Contact & Address"
              subtitle="Support contact and physical address"
              icon={<PhoneIcon />}
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Support Email" placeholder="support@intalk.ca"
                    value={form.support_email || ""} onChange={(e: any) => set("support_email")(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Support Phone" placeholder="(555) 000-0000"
                    value={form.support_phone || ""} onChange={(e: any) => set("support_phone")(e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Address" placeholder="Street address"
                    value={form.address || ""} onChange={(e: any) => set("address")(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="City"
                    value={form.city || ""} onChange={(e: any) => set("city")(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="Province / State"
                    value={form.province || ""} onChange={(e: any) => set("province")(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="Country"
                    value={form.country || ""} onChange={(e: any) => set("country")(e.target.value)} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth size="small" label="Postal Code"
                    value={form.postal_code || ""} onChange={(e: any) => set("postal_code")(e.target.value)} />
                </Grid>
              </Grid>
            </FormCard>
          </Box>

          <Box mb={3}>
            <FormCard
              title="Logo Settings"
              subtitle="Logos used throughout the platform"
              icon={<ImageIcon />}
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <ImageField label="Main Logo (sidebar, header, reports, emails)" value={form.logo_url || ""} onChange={set("logo_url")} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ImageField label="Small Logo (collapsed sidebar)" value={form.small_logo_url || ""} onChange={set("small_logo_url")} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ImageField label="Favicon (browser tab)" value={form.favicon_url || ""} onChange={set("favicon_url")} />
                </Grid>
              </Grid>
            </FormCard>
          </Box>

          <Box mb={3}>
            <FormCard
              title="Login Page"
              subtitle="Logo and background for the login screen"
              icon={<LoginIcon />}
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <ImageField label="Login Logo (defaults to main logo)" value={form.login_logo_url || ""} onChange={set("login_logo_url")} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ImageField label="Login Background Image" value={form.login_background_url || ""} onChange={set("login_background_url")} tall />
                </Grid>
              </Grid>
            </FormCard>
          </Box>
        </Grid>

        {/* Right column — Theme Builder */}
        <Grid item xs={12} lg={5}>
          <Box mb={3}>
            <FormCard
              title="Theme Builder"
              subtitle="Customise the colour palette for your white-label"
              icon={<PaletteIcon />}
            >
              <Grid container spacing={2.5}>
                <Grid item xs={6}><ColorField label="Primary" value={form.primary_color} onChange={set("primary_color")} /></Grid>
                <Grid item xs={6}><ColorField label="Secondary" value={form.secondary_color} onChange={set("secondary_color")} /></Grid>
                <Grid item xs={6}><ColorField label="Sidebar Background" value={form.sidebar_background} onChange={set("sidebar_background")} /></Grid>
                <Grid item xs={6}><ColorField label="Sidebar Text" value={form.sidebar_text_color} onChange={set("sidebar_text_color")} /></Grid>
                <Grid item xs={6}><ColorField label="Top Bar Background" value={form.topbar_background} onChange={set("topbar_background")} /></Grid>
                <Grid item xs={6}><ColorField label="Top Bar Text" value={form.topbar_text_color} onChange={set("topbar_text_color")} /></Grid>
                <Grid item xs={6}><ColorField label="Button" value={form.button_color} onChange={set("button_color")} /></Grid>
                <Grid item xs={6}><ColorField label="Button Text" value={form.button_text_color} onChange={set("button_text_color")} /></Grid>
                <Grid item xs={6}><ColorField label="Card Background" value={form.card_background} onChange={set("card_background")} /></Grid>
                <Grid item xs={6}><ColorField label="Page Background" value={form.background_color} onChange={set("background_color")} /></Grid>
                <Grid item xs={6}><ColorField label="Text" value={form.text_color} onChange={set("text_color")} /></Grid>
              </Grid>

              <Divider sx={{ my: 2.5 }} />

              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 0.75 }}>
                Border Radius — {form.border_radius}px
              </Typography>
              <Slider size="small" min={0} max={24} value={form.border_radius}
                onChange={(_, v) => set("border_radius")(v as number)}
                sx={{ color: NAVY, mb: 1 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 0.75 }}>
                Card Border Radius — {form.card_border_radius}px
              </Typography>
              <Slider size="small" min={0} max={32} value={form.card_border_radius}
                onChange={(_, v) => set("card_border_radius")(v as number)}
                sx={{ color: NAVY }} />
            </FormCard>
          </Box>

          {/* Live preview */}
          <FormCard
            title="Preview"
            subtitle="Live theme preview — updates as you change colours"
            icon={<PaletteIcon />}
          >
            <Box sx={{ borderRadius: `${form.card_border_radius}px`, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
              <Box sx={{ display: "flex" }}>
                <Box sx={{ width: 70, bgcolor: form.sidebar_background, color: form.sidebar_text_color, p: 1.5, minHeight: 150 }}>
                  <Typography fontSize={10} fontWeight={700} noWrap>{form.company_name}</Typography>
                  {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{ mt: 1.5, height: 6, borderRadius: 1, bgcolor: form.sidebar_text_color, opacity: 0.5 }} />
                  ))}
                </Box>
                <Box sx={{ flex: 1, bgcolor: form.background_color, color: form.text_color }}>
                  <Box sx={{ bgcolor: form.topbar_background, color: form.topbar_text_color, px: 1.5, py: 1 }}>
                    <Typography fontSize={11} fontWeight={600}>Dashboard</Typography>
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    <Box sx={{ bgcolor: form.card_background, borderRadius: `${form.card_border_radius}px`, p: 1.5, border: "1px solid rgba(0,0,0,0.06)" }}>
                      <Typography fontSize={11} sx={{ mb: 1 }}>Sample card content</Typography>
                      <Button size="small" disableElevation variant="contained"
                        sx={{ bgcolor: form.button_color, color: form.button_text_color, borderRadius: `${form.border_radius}px`, textTransform: "none", fontSize: 11, "&:hover": { bgcolor: form.primary_color } }}>
                        Primary Button
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Tooltip title="Colours are exposed app-wide as CSS variables (--primary-color, --sidebar-bg, …) after saving.">
              <Typography fontSize={11} color="text.secondary" sx={{ mt: 1.5, cursor: "help" }}>
                Saved colours apply instantly via CSS variables — no rebuild needed.
              </Typography>
            </Tooltip>
          </FormCard>
        </Grid>
      </Grid>
    </AppPage>
  );
};

export default CompanyBranding;
